// The result shape every standalone `src/lib` client resolves to: no throws, every status
// classified. Lives on its own so the relay uploader and the feature clients can share it
// without importing each other.

export type ApiResult<T> =
	| { kind: 'ok'; status: number; data: T }
	/** No response at all (offline, timeout, CORS). Retryable. */
	| { kind: 'offline'; status: 0 }
	/** 5xx, 408, 429 — the server may accept the same request later. Retryable. */
	| { kind: 'retry'; status: number; code?: string; message?: string }
	| { kind: 'conflict'; status: 409; code?: string; message?: string }
	/** Any other 4xx — sending the same bytes again cannot succeed. */
	| { kind: 'rejected'; status: number; code?: string; message?: string };

export const isRetryable = (r: ApiResult<unknown>): boolean => r.kind === 'offline' || r.kind === 'retry';

export const resultMessage = (r: ApiResult<unknown>): string | undefined => ('message' in r ? r.message : undefined);

const readCode = (body: unknown): string | undefined => {
	if (!body || typeof body !== 'object') return undefined;
	const { data, code } = body as { data?: unknown; code?: unknown };
	if (data && typeof data === 'object' && typeof (data as { code?: unknown }).code === 'string') {
		return (data as { code: string }).code;
	}
	return typeof code === 'string' ? code : undefined;
};

const readMessage = (body: unknown): string | undefined => {
	if (!body || typeof body !== 'object') return undefined;
	const { message, title, errors } = body as { message?: unknown; title?: unknown; errors?: Record<string, unknown> };
	if (typeof message === 'string' && message) return message;
	if (errors && typeof errors === 'object') {
		for (const value of Object.values(errors)) {
			if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
		}
	}
	return typeof title === 'string' ? title : undefined;
};

/**
 * Turns `(status, body)` into an {@link ApiResult}. `pick` extracts the payload from the
 * Core envelope's `data`; a 2xx whose payload it cannot read is `retry`, never `ok` with
 * undefined (an HTML fallback page answers 200 too).
 */
export const classifyResponse = <T>(status: number, body: unknown, pick: (data: unknown) => T | undefined): ApiResult<T> => {
	if (!status) return { kind: 'offline', status: 0 };
	if (status >= 200 && status < 300) {
		const data = body && typeof body === 'object' ? pick((body as { data?: unknown }).data) : undefined;
		return data === undefined ? { kind: 'retry', status, message: 'Unreadable response' } : { kind: 'ok', status, data };
	}
	const code = readCode(body);
	const message = readMessage(body);
	if (status === 409) return { kind: 'conflict', status: 409, code, message };
	if (status >= 500 || status === 408 || status === 429) return { kind: 'retry', status, code, message };
	return { kind: 'rejected', status, code, message };
};
